import { useEffect, useState, useRef } from 'react'
import { useAuthFromContext } from '../AuthContext'
import * as data from '../data/prototypeData'
import { ARMIES_PER_ROUND, BATTLEFIELD_COUNT, BATTLEFIELD_CAPACITIES,
         ROCKET_COST, DRONE_COST, DRONE_FLIGHT_MS, DRONE_HOVER_MS,
         REPLAY_LEAD_MS, BASE_BUILD_WIGGLE_MS, BASE_BUILD_FLIGHT_MS,
         missileImpactMs, missileFlightTotalMs, MISSILE_SURPLUS_STAGGER_MS } from '../rules/gameConfig'
import { initSound, playSound } from '../utils/soundManager'
import {
    canAttack,
    moveUnit,
    undoMove,
    deployArmy,
    undoDeploy,
    buildFort,
    undoBuildFort,
    requiredDropZones,
    canPlaceDropZone,
    fittingDropZones,
    buildOppReplaySteps,
    matureCorridor,
} from '../rules/gameLogic'
import { plotOf, isStructureDestroyed, battlefieldColumn } from '../rules/subplotGrid'
import { canPlaceBase, addBrood, baseFootprint } from '../rules/subplotBases'
import { applyHiveDelta } from '../rules/hiveDelta'
import { validateOrders } from '../rules/validateOrders'

// ─── useServerWar ────────────────────────────────────────────────────────────
//
// The war screen's state for a war the server runs (functions/src/wars.js,
// rules/warHost.js), in the shape BattleScreen takes — useGameState's, for
// the progression prototype. The server resolves every round; this hook only
// plans the player's orders, sends them, and plays back what the server
// says happened:
//   view     wars/{id}/views/{seat}: the player's side whole, the other side
//            as the board shows it, the round, who has submitted
//   reveals  wars/{id}/views/{seat}/reveals/{n}: round n played back in this
//            player's terms (warHost.revealFor), the Hives as differences
//            from the round's opening (hiveDelta)
// The planning half (moves, deploys, Broods, rockets, planes, targets, drop
// zones) and the reveal's animation are useGameState's, unchanged; what's
// gone is the client-side resolution, the RTDB persistence and the bot.
// (When the prototype replaces the old flow, useGameState goes.)
//
// A round's reveal is "watched" once the player presses REVEAL; the last
// watched round is kept per war in this browser's localStorage, so a reload
// doesn't replay it. Only the latest resolved round is ever played: rounds
// the server played for an absent player (a missed async turn) are skipped.

const watchedKey = (warId) => `hog.watched.${warId}`
function readWatched(warId) {
    try { return Number(window.localStorage.getItem(watchedKey(warId))) || 0 } catch { return 0 }
}
function writeWatched(warId, n) {
    try { window.localStorage.setItem(watchedKey(warId), String(n)) } catch { /* the reveal just replays on reload */ }
}

// A Hive from the server (an object, or a JSON string), as a copy this hook may change.
const hfOf = (hf) => (hf == null ? null : typeof hf === 'string' ? JSON.parse(hf) : JSON.parse(JSON.stringify(hf)))
const noTargets = () => Array(BATTLEFIELD_COUNT).fill(null)
export const warResultFor = (result, seat) => (!result ? null : result.draw ? 'draw' : result.winner === seat ? 'win' : 'loss')

export default function useServerWar(warId) {
    const { user } = useAuthFromContext()
    const uid = user?.uid ?? null

    // Preload battle SFX (creates a suspended audio context); handleReveal resumes it.
    useEffect(() => { initSound() }, [])
    // Opening the war asks the server to catch it up (wars.catchUpWar): a
    // move saved but never counted, or a bot move still owed, goes in now.
    useEffect(() => { data.catchUpWar(warId).catch(() => {}) }, [warId])

    // ── The server's war ──────────────────────────────────────────────────────
    const [war, setWar] = useState(null)
    const [view, setView] = useState(null)
    const viewRef = useRef(null)
    const seat = war && uid ? (war.players.p1.uid === uid ? 'p1' : war.players.p2.uid === uid ? 'p2' : null) : null

    useEffect(() => data.watchWar(warId, setWar), [warId])
    useEffect(() => {
        if (!seat) return undefined
        return data.watchView(warId, seat, (v) => { viewRef.current = v; setView(v) })
    }, [warId, seat])

    // ── Planning state ────────────────────────────────────────────────────────
    const [selfLocalBoardArr, setSelfLocalBoardArr] = useState([])
    const [armiesToDeploy, setArmiesToDeploy] = useState(0)
    const [oppLocalBoardArr, setOppLocalBoardArr] = useState([])
    const [yieldBalance, setYieldBalance] = useState(0)
    const [selfHf, setSelfHf] = useState(null)
    const [oppHf, setOppHf] = useState(null)
    const [selfLost, setSelfLost] = useState(0)
    const [oppLost, setOppLost] = useState(0)
    const [targetPlots, setTargetPlots] = useState(noTargets)
    const [targetSubplots, setTargetSubplots] = useState(noTargets)
    const [dropZones, setDropZones] = useState([])
    const [pendingRockets, setPendingRockets] = useState([])
    const [pendingDrones, setPendingDrones] = useState([])
    const [message, setMessage] = useState('')
    const [warResult, setWarResult] = useState(null)
    // Orders sent that the server hasn't taken (or refused) yet, and the plan
    // as it stood before submit matured it (put back on a refusal).
    const [submitting, setSubmitting] = useState(false)
    const planBeforeSubmitRef = useRef(null)
    const submittingRoundRef = useRef(null)
    // The round the planning state belongs to: a new round resets it.
    const plannedRoundRef = useRef(null)

    // ── Reveal state (useGameState's) ─────────────────────────────────────────
    const [animationEvents, setAnimationEvents] = useState([])
    const [animBoardSelf, setAnimBoardSelf] = useState(null)
    const [animBoardOpp, setAnimBoardOpp] = useState(null)
    const [replayBoardOpp, setReplayBoardOpp] = useState(null)
    const replayDataRef = useRef(null)
    const oppFreshBuildsRef = useRef(null)
    const baseBuildFxKeyRef = useRef(0)
    const [baseBuildFx, setBaseBuildFx] = useState(null)
    const [showingPreResolution, setShowingPreResolution] = useState(false)
    const [isAnimating, setIsAnimating] = useState(false)
    const [activeClashId, setActiveClashId] = useState(null)
    const [activeClashContext, setActiveClashContext] = useState(null)
    const [activeClashWinner, setActiveClashWinner] = useState(null)
    const [activeClashSelfStrength, setActiveClashSelfStrength] = useState(null)
    const [activeClashOppStrength, setActiveClashOppStrength] = useState(null)
    const [activeClashOppTargetIndex, setActiveClashOppTargetIndex] = useState(null)
    const [activeClashSelfTargetIndex, setActiveClashSelfTargetIndex] = useState(null)
    const [activeClashAbsorbed, setActiveClashAbsorbed] = useState(0)
    const [activeClashAimS, setActiveClashAimS] = useState(null)
    const [clashResult, setClashResult] = useState(null)
    const [animLost, setAnimLost] = useState(null)
    const [droneScans, setDroneScans] = useState(null)
    const [droneFlights, setDroneFlights] = useState(null)
    const [resupplyDeliveries, setResupplyDeliveries] = useState(null)
    const animationAbortedRef = useRef(false)
    // { warResult } of the round being revealed, applied when its reveal ends.
    const pendingResolutionRef = useRef(null)
    // The targets this player fought the revealed round with (the reveal's `own`).
    const revealTargetsRef = useRef(noTargets())

    // 'plan' (the view drives the screen), 'loading' (fetching a reveal) or
    // 'reveal' (a reveal is set up or playing: the view waits).
    const phaseRef = useRef('plan')
    const [watched, setWatched] = useState(() => readWatched(warId))
    const watchedRef = useRef(watched)
    const [prepared, setPrepared] = useState(null)     // the round number set up to reveal

    useEffect(() => () => { animationAbortedRef.current = true }, [])

    function markWatched(n) {
        watchedRef.current = n
        setWatched(n)
        writeWatched(warId, n)
    }

    // ── The view drives the screen, unless a reveal is up ────────────────────
    useEffect(() => {
        if (view && seat) sync(view)
    }, [view, seat]) // eslint-disable-line react-hooks/exhaustive-deps

    function sync(v) {
        if (phaseRef.current !== 'plan') return
        const latest = v.roundNumber - 1
        if (latest >= 1 && latest > watchedRef.current) {
            phaseRef.current = 'loading'
            data.loadReveal(warId, seat, latest)
                .then(r => { if (r) prepareReveal(r); else skipReveal(latest) })
                .catch(e => { console.error('Reveal failed to load', e); skipReveal(latest) })
            return
        }
        applyView(v)
    }

    function skipReveal(n) {
        markWatched(n)
        phaseRef.current = 'plan'
        if (viewRef.current) applyView(viewRef.current)
    }

    function applyView(v) {
        const pending = v.self.pending
        setSelfHf(hfOf(pending?.homefront ?? v.self.homefront))
        setOppHf(hfOf(v.opp.homefront))
        setSelfLost(v.self.structuresLost ?? 0)
        setOppLost(v.opp.structuresLost ?? 0)
        setOppLocalBoardArr(v.opp.board)
        setYieldBalance((pending ?? v.self).yield ?? 0)
        if (plannedRoundRef.current !== v.roundNumber) {
            plannedRoundRef.current = v.roundNumber
            setSelfLocalBoardArr(pending?.board ?? v.self.board)
            setArmiesToDeploy(pending ? 0 : ARMIES_PER_ROUND)
            setTargetPlots(noTargets())
            setTargetSubplots(noTargets())
            setDropZones([])
            setPendingRockets([])
            setPendingDrones([])
            planBeforeSubmitRef.current = null
        } else if (pending) {
            setSelfLocalBoardArr(pending.board)
        }
        if (v.status !== 'active') setWarResult(warResultFor(v.result, seat))
    }

    // The server took the orders (this seat shows submitted, or the round
    // resolved straight away), or refused them: back to planning, with its
    // reason.
    useEffect(() => {
        if (!view || !submitting) return
        if (view.submitted?.[seat] || view.roundNumber !== submittingRoundRef.current) {
            setSubmitting(false)
            return
        }
        const err = view.lastError
        if (!err || err.round !== view.roundNumber) return
        setSubmitting(false)
        if (planBeforeSubmitRef.current) setSelfLocalBoardArr(planBeforeSubmitRef.current)
        planBeforeSubmitRef.current = null
        setMessage(err.message)
    }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

    // A resolved round, set up under the READY overlay as useGameState's
    // computeAttacks set it up: both Hives and boards as the round opened,
    // this player's new Broods on their Hive, the other's held for the replay.
    function prepareReveal(R) {
        const beforeSelf = hfOf(R.before.self)
        const beforeOpp = hfOf(R.before.opp)
        const self = (d) => applyHiveDelta(beforeSelf, d)
        const opp = (d) => applyHiveDelta(beforeOpp, d)
        const events = R.events.map(e => {
            if (!e) return e
            const out = { ...e }
            if (e.postClashSelfHf) out.postClashSelfHf = self(e.postClashSelfHf)
            if (e.postClashOppHf) out.postClashOppHf = opp(e.postClashOppHf)
            if (e.craterStages) out.craterStages = e.craterStages.map(e.killSide === 'opp' ? opp : self)
            return out
        })
        // resolveRound's counter for the round (warHost: round = roundNumber + 1).
        const round = R.roundNumber + 1

        setResupplyDeliveries({
            self: R.mine.delivery.delivered, opp: R.theirs.delivery.delivered,
            sources: { self: R.mine.delivery.sources, opp: R.theirs.delivery.sources },
        })
        const scans = { self: R.mine.scans ?? [], opp: (R.theirs.scansOnMe ?? []).map(s => ({ ...s, launchS: null })) }
        setDroneScans(scans.self.length || scans.opp.length ? scans : null)
        pendingResolutionRef.current = { warResult: !R.outcome.over ? null : R.outcome.draw ? 'draw' : R.outcome.winner === seat ? 'win' : 'loss' }
        revealTargetsRef.current = R.own?.targetPlots ?? noTargets()

        setAnimationEvents(events)
        setAnimBoardSelf(R.mine.boardAtOpen)
        setAnimBoardOpp(R.theirs.boardAtOpen)
        replayDataRef.current = { fromBoard: R.theirs.boardBefore ?? null, ledger: R.theirs.actions ?? null }
        setReplayBoardOpp(R.theirs.boardBefore ?? null)
        setAnimLost({ self: R.lostBefore.self, opp: R.lostBefore.opp })
        setSelfLost(R.lostBefore.self)
        setOppLost(R.lostBefore.opp)

        const displaySelf = hfOf(beforeSelf)
        for (const b of R.own?.baseBuilds ?? []) {
            if (b?.centerS != null && b.col != null) addBrood(displaySelf, b.col, b.centerS, round)
        }
        oppFreshBuildsRef.current = { round, builds: (R.theirs.baseBuilds ?? []).filter(b => b?.col != null && b?.centerS != null) }
        setSelfHf(displaySelf)
        setOppHf(hfOf(beforeOpp))
        setShowingPreResolution(true)
        phaseRef.current = 'reveal'
        setPrepared(R.roundNumber)
    }

    async function handleReveal() {
        const latest = viewRef.current ? viewRef.current.roundNumber - 1 : 0
        if (prepared == null || prepared !== latest || !animationEvents.length) {
            console.warn('REVEAL ignored — round', latest, 'is not set up yet.')
            return false
        }
        markWatched(prepared)
        if (war?.mode === 'sync') data.revealWatched(warId).catch(e => console.error('revealWatched failed', e))
        initSound()  // REVEAL click is the user gesture that unlocks/resumes audio
        animationAbortedRef.current = false
        setIsAnimating(true)
        const delay = ms => new Promise(res => setTimeout(res, ms))
        const targets = revealTargetsRef.current
        await playOppReplay(delay)
        mergeRemainingOppBases()
        if (animationAbortedRef.current) return false
        for (let eventCol = 0; eventCol < animationEvents.length; eventCol++) {
            const event = animationEvents[eventCol]
            if (animationAbortedRef.current) return false
            if (!event || event.type === 'NO_BATTLE') continue
            const { battlefieldId, type } = event
            const applyLedger = (prev, ledger) =>
                (prev && ledger) ? prev.map(bf => (bf.id === battlefieldId ? { ...bf, ...ledger } : bf)) : prev
            setAnimBoardSelf(prev => applyLedger(prev, event.selfLedgerAtClash))
            setAnimBoardOpp(prev => applyLedger(prev, event.oppLedgerAtClash))
            setActiveClashId(battlefieldId)
            setActiveClashContext(event.context)
            setActiveClashWinner(type === 'WIN' ? 'self' : type === 'LOSS' ? 'opp' : 'tie')
            setActiveClashSelfStrength(event.selfStrength)
            setActiveClashOppStrength(event.oppStrength)
            setActiveClashOppTargetIndex(
                type === 'LOSS' ? (event.oppTargetIndex ?? event.retaliationRow ?? null) : null
            )
            setActiveClashSelfTargetIndex(
                type === 'WIN' ? ((targets[eventCol] ?? null) !== null ? targets[eventCol] : (event.retaliationRow ?? null)) : null
            )
            setActiveClashAbsorbed(event.absorbed ?? 0)
            setActiveClashAimS(event.aimS ?? null)
            setClashResult(null)
            const struckStructure = (event.destroyedStructures?.length ?? 0) > 0
            const winnerHasTarget =
                (type === 'WIN'  && ((targets[eventCol] ?? null) !== null || (event.retaliationRow ?? null) !== null)) ||
                (type === 'LOSS' && ((event.oppTargetIndex ?? null) !== null || (event.retaliationRow ?? null) !== null))
            if (type !== 'TIE') {
                const pairCount = Math.min(event.selfStrength ?? 0, event.oppStrength ?? 0)
                const missileArrivalMs = winnerHasTarget
                    ? missileImpactMs(pairCount, event.absorbed ?? 0)
                    : 900
                // The crater grows one stage per landing shot (useGameState).
                if (event.postClashOppHf || event.postClashSelfHf) {
                    const swapAt = (hf, ms) => setTimeout(() => {
                        if (animationAbortedRef.current) return
                        if (event.postClashOppHf)  setOppHf(hf)
                        if (event.postClashSelfHf) setSelfHf(hf)
                    }, ms)
                    const stages = event.craterStages ?? []
                    stages.forEach((hf, i) => swapAt(hf, missileArrivalMs + i * MISSILE_SURPLUS_STAGGER_MS))
                    swapAt(event.postClashOppHf ?? event.postClashSelfHf,
                           missileArrivalMs + stages.length * MISSILE_SURPLUS_STAGGER_MS)
                }
            }
            const lastMissileMs = missileFlightTotalMs(event.selfStrength, event.oppStrength, winnerHasTarget) + 450
            await delay(Math.max(struckStructure ? 3000 : 1800, lastMissileMs))
            if (animationAbortedRef.current) return false
            setClashResult(event.washedByRockets ? 'TIE' : type)
            await delay(1680)
            if (animationAbortedRef.current) return false
            setAnimBoardSelf(prev => {
                if (!prev) return prev
                return prev.map(bf => {
                    if (bf.id !== battlefieldId) return bf
                    return { ...bf, armyQty: 0, fortCount: 0, fortInConstruction: 0, newArmies: 0, deliveredNewArmies: 0, movedInArmies: 0, isAttacking: false }
                })
            })
            setAnimBoardOpp(prev => {
                if (!prev) return prev
                return prev.map(bf => {
                    if (bf.id !== battlefieldId) return bf
                    return { ...bf, armyQty: 0, fortCount: 0, fortInConstruction: 0 }
                })
            })
            setActiveClashId(null)
            setActiveClashContext(null)
            setActiveClashWinner(null)
            setActiveClashSelfStrength(null)
            setActiveClashOppStrength(null)
            setActiveClashOppTargetIndex(null)
            setActiveClashSelfTargetIndex(null)
            setActiveClashAbsorbed(0)
            setActiveClashAimS(null)
            setClashResult(null)
        }
        if (animationAbortedRef.current) return false

        // ── Drone surveillance phase ── (useGameState's): the post-scan Hives
        // — the round's end, the view's — swap in at touchdown.
        if (droneScans && (droneScans.self.length > 0 || droneScans.opp.length > 0)) {
            setDroneFlights(droneScans)
            const droneCount = droneScans.self.length + droneScans.opp.length
            playSound('drone', {
                gainScale: Math.min(1 + 0.15 * (droneCount - 1), 1.3),
                duration: (DRONE_FLIGHT_MS + DRONE_HOVER_MS) / 1000,
            })
            await delay(DRONE_FLIGHT_MS)
            if (animationAbortedRef.current) return false
            const v = viewRef.current
            if (v) {
                setSelfHf(hfOf(v.self.homefront))
                setOppHf(hfOf(v.opp.homefront))
            }
            await delay(DRONE_HOVER_MS)
            if (animationAbortedRef.current) return false
            setDroneFlights(null)
        }
        setDroneScans(null)

        // The round is over on screen: the view takes over (the next round's
        // board, the Hives after the round, the kill counts).
        const resolved = pendingResolutionRef.current
        pendingResolutionRef.current = null
        setIsAnimating(false)
        setShowingPreResolution(false)
        setAnimLost(null)
        setAnimationEvents([])
        setAnimBoardSelf(null)
        setAnimBoardOpp(null)
        setPrepared(null)
        phaseRef.current = 'plan'
        if (viewRef.current) sync(viewRef.current)
        if (resolved?.warResult) setWarResult(resolved.warResult)
        return true
    }

    // useGameState's opponent replay: their half steps through their round
    // (moves, new Broods, then fresh deploys) before the clashes.
    async function playOppReplay(delay) {
        const replay = replayDataRef.current
        replayDataRef.current = null
        try {
            const steps = replay?.fromBoard && animBoardOpp
                ? buildOppReplaySteps(replay.fromBoard, animBoardOpp, replay.ledger)
                : null
            if (!steps) return
            await delay(REPLAY_LEAD_MS)
            for (const step of steps) {
                if (animationAbortedRef.current) return
                const builds = step.buildCol != null
                    ? (oppFreshBuildsRef.current?.builds ?? []).filter(b => b.col === step.buildCol)
                    : []
                if (builds.length) {
                    setBaseBuildFx({
                        col: step.buildCol,
                        builds: builds.map((b, i) => ({
                            centerS: b.centerS,
                            uids: step.buildPairs?.[i] ?? [],
                        })),
                        key: ++baseBuildFxKeyRef.current,
                    })
                    await delay(50)
                    setReplayBoardOpp(step.board)
                    const preLand = Math.max(0, Math.min(step.holdMs, BASE_BUILD_WIGGLE_MS + BASE_BUILD_FLIGHT_MS - 130) - 50)
                    await delay(preLand)
                    mergeOppFreshBase(step.buildCol)
                    await delay(Math.max(0, step.holdMs - preLand - 50))
                } else {
                    setReplayBoardOpp(step.board)
                    await delay(step.holdMs)
                }
            }
        } finally {
            setReplayBoardOpp(null)
            setBaseBuildFx(null)
        }
    }

    function mergeOppFreshBase(col) {
        const fresh = oppFreshBuildsRef.current
        const builds = (fresh?.builds ?? []).filter(x => x.col === col)
        if (!builds.length) return
        fresh.builds = fresh.builds.filter(x => x.col !== col)
        setOppHf(prev => {
            if (!prev) return prev
            const hf = hfOf(prev)
            for (const b of builds) addBrood(hf, b.col, b.centerS, fresh.round)
            return hf
        })
    }
    function mergeRemainingOppBases() {
        const fresh = oppFreshBuildsRef.current
        if (!fresh?.builds?.length) return
        const builds = fresh.builds
        fresh.builds = []
        setOppHf(prev => {
            if (!prev) return prev
            const hf = hfOf(prev)
            for (const b of builds) addBrood(hf, b.col, b.centerS, fresh.round)
            return hf
        })
    }

    function markAnimKill(side) {
        setAnimLost(prev => prev ? { ...prev, [side]: (prev[side] ?? 0) + 1 } : prev)
    }

    // ── Overlay window ────────────────────────────────────────────────────────
    // SUBMITTED from this player's submit until the round resolves; READY /
    // REVEAL from then until REVEAL is pressed.
    const latestResolved = view ? view.roundNumber - 1 : 0
    const unwatched = latestResolved >= 1 && latestResolved > watched
    const submittedHere = !!view?.submitted?.[seat] || submitting
    const ended = view != null && view.status !== 'active'
    const revealOverlayUp = !isAnimating && (unwatched || (submittedHere && !ended))
    const oppReadyOverlayUp = revealOverlayUp && unwatched
    const isSubmitDisabled = !view || submittedHere || unwatched || ended

    // ── Action dispatcher (useGameState's) ────────────────────────────────────
    const config = war?.config ?? null
    const rocketCost = config?.rocketCost ?? ROCKET_COST
    const droneCost = config?.droneCost ?? DRONE_COST

    function onAction(bfId, action, extras) {
        if (warResult !== null || isSubmitDisabled) return
        switch (action) {
            case 'attack': {
                const col = selfLocalBoardArr.findIndex(bf => bf.id === bfId)
                const bf = selfLocalBoardArr[col]
                if (!bf || bf.isAttacking) break
                const { valid, reason } = canAttack(bf)
                if (!valid) { setMessage(reason); break }
                const next = selfLocalBoardArr.map((b, i) => (i === col ? { ...b, isAttacking: true } : b))
                setSelfLocalBoardArr(next)
                // An attacking corridor's broods fire and owe no drop zone.
                const owed = Math.min(requiredDropZones(next), openSlotCount)
                setDropZones(prev => (prev.length > owed ? prev.slice(0, owed) : prev))
                break
            }

            case 'undoAttack': {
                const undoCol = selfLocalBoardArr.findIndex(bf => bf.id === bfId)
                setSelfLocalBoardArr(prev => prev.map(bf =>
                    bf.id !== bfId ? bf : { ...bf, isAttacking: false }
                ))
                if (undoCol !== -1) {
                    setTargetPlots(prev => { const next = [...prev]; next[undoCol] = null; return next })
                    setTargetSubplots(prev => { const next = [...prev]; next[undoCol] = null; return next })
                }
                break
            }

            case 'move': {
                const { success, reason, boardArr } = moveUnit(selfLocalBoardArr, bfId, extras?.direction, extras?.uid ?? null)
                if (!success) { setMessage(reason); break }
                setSelfLocalBoardArr(boardArr)
                break
            }

            case 'undoMove':
                setSelfLocalBoardArr(prev => {
                    const { success, reason, boardArr } = undoMove(prev, bfId, extras?.uid ?? null)
                    if (!success) { setMessage(reason); return prev }
                    return boardArr
                })
                break

            case 'buildFort':
                setSelfLocalBoardArr(prev => {
                    const col = prev.findIndex(bf => bf.id === bfId)
                    const centerS = extras?.centerS ?? null
                    const pendingCells = col === -1 ? new Set() : new Set(
                        (prev[col].pendingBaseBuilds ?? []).flatMap(p => baseFootprint(p.centerS) ?? [])
                    )
                    const footprint = baseFootprint(centerS) ?? []
                    if (col === -1 || !canPlaceBase(selfHf, col, centerS) ||
                        footprint.some(s => pendingCells.has(s))) {
                        setMessage('Pick a clear spot in this corridor for your brood.')
                        return prev
                    }
                    const { success, reason, boardArr } = buildFort(prev, bfId, centerS, extras?.uids ?? null)
                    if (!success) { setMessage(reason); return prev }
                    return boardArr
                })
                break

            case 'undoBuildFort':
                setSelfLocalBoardArr(prev => {
                    const { success, reason, boardArr } = undoBuildFort(prev, bfId, extras?.centerS ?? null)
                    if (!success) { setMessage(reason); return prev }
                    return boardArr
                })
                break

            case 'deploy': {
                const result = deployArmy(selfLocalBoardArr, bfId, armiesToDeploy)
                if (!result.success) { setMessage(result.reason); return }
                setSelfLocalBoardArr(result.boardArr)
                setArmiesToDeploy(result.armiesToDeploy)
                break
            }

            case 'remove': {
                const result = undoDeploy(selfLocalBoardArr, bfId, armiesToDeploy)
                if (!result.success) { setMessage(result.reason); break }
                setSelfLocalBoardArr(result.boardArr)
                setArmiesToDeploy(result.armiesToDeploy)
                break
            }

            default:
                break
        }
    }

    // Rocket / plane buys on an own structure cell, pending until submit.
    const pendingSpend = pendingRockets.length * rocketCost + pendingDrones.length * droneCost

    function onHomefrontAction(subS, action) {
        if (warResult !== null || isSubmitDisabled) return
        const cell = selfHf?.subplots?.[subS]
        const structureId = cell?.structureId
        if (structureId == null || cell.destroyed) return
        if (isStructureDestroyed(selfHf, structureId)) return

        const hasDeployedRocket = (selfHf.structures[structureId]?.rocketCells || []).includes(subS)
        const hasPendingDrone   = pendingDrones.some(d => d.launchS === subS)

        if (action === 'buyRocket') {
            if (hasDeployedRocket || pendingRockets.includes(subS)) { setMessage('That subplot already has a rocket.'); return }
            if (hasPendingDrone) { setMessage('That subplot is launching a recon plane this round.'); return }
            const spendable = yieldBalance - pendingSpend
            if (spendable < rocketCost) { setMessage(`Need ${rocketCost} yield to buy a rocket (have ${spendable}).`); return }
            setPendingRockets(prev => [...prev, subS])
        } else if (action === 'undoRocket') {
            setPendingRockets(prev => {
                const i = prev.lastIndexOf(subS)
                if (i === -1) return prev
                const next = [...prev]
                next.splice(i, 1)
                return next
            })
        } else if (action === 'buyDrone') {
            if (hasDeployedRocket || pendingRockets.includes(subS)) { setMessage('That subplot already hosts a rocket.'); return }
            if (hasPendingDrone) { setMessage('That subplot is already launching a recon plane.'); return }
            const spendable = yieldBalance - pendingSpend
            if (spendable < droneCost) { setMessage(`Need ${droneCost} yield to buy a recon plane (have ${spendable}).`); return }
            setPendingDrones(prev => [...prev, { launchS: subS, targetS: null }])
        } else if (action === 'undoDrone') {
            setPendingDrones(prev => {
                const i = prev.map(d => d.launchS).lastIndexOf(subS)
                if (i === -1) return prev
                const next = [...prev]
                next.splice(i, 1)
                return next
            })
        }
    }

    function onDroneTarget(col, subS) {
        if (warResult !== null || revealOverlayUp) return
        if (battlefieldColumn(subS) !== col) return
        setPendingDrones(prev => {
            const inCol = (d) => battlefieldColumn(d.launchS) === col
            let idx = prev.findIndex(d => inCol(d) && d.targetS == null)
            if (idx === -1) {
                for (let i = prev.length - 1; i >= 0; i--) {
                    if (inCol(prev[i])) { idx = i; break }
                }
            }
            if (idx === -1) return prev
            const next = [...prev]
            next[idx] = { ...next[idx], targetS: subS }
            return next
        })
    }

    const openSlotCount = selfLocalBoardArr.reduce((sum, bf) =>
        sum + Math.max(0, bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))), 0)
    const requiredDropZoneCount = Math.min(requiredDropZones(selfLocalBoardArr), openSlotCount)

    useEffect(() => {
        if (!dropZones.length) return
        const kept = fittingDropZones(selfLocalBoardArr, dropZones)
        if (kept === dropZones) return
        setDropZones(kept)
        const n = dropZones.length - kept.length
        setMessage(`A drone took ${n === 1 ? 'a drop zone\'s slot' : `${n} drop zones' slots`}. Place ${n === 1 ? 'it' : 'them'} again.`)
    }, [selfLocalBoardArr]) // eslint-disable-line react-hooks/exhaustive-deps

    const placingDropZones = warResult === null && !isSubmitDisabled && !showingPreResolution &&
        armiesToDeploy === 0 && requiredDropZoneCount > 0 && dropZones.length < requiredDropZoneCount

    function onDropZone(col, action) {
        if (warResult !== null || isSubmitDisabled) return
        if (action === 'place') {
            if (dropZones.length >= requiredDropZoneCount) { setMessage('All drop zones placed.'); return }
            const { valid, reason } = canPlaceDropZone(selfLocalBoardArr, col, dropZones)
            if (!valid) { setMessage(reason); return }
            setDropZones(prev => [...prev, col])
        } else if (action === 'remove') {
            setDropZones(prev => {
                const i = prev.lastIndexOf(col)
                if (i === -1) return prev
                const next = [...prev]
                next.splice(i, 1)
                return next
            })
        }
    }

    function onTargetPlot(col, subS) {
        if (warResult !== null || revealOverlayUp) return
        setTargetSubplots(prev => { const next = [...prev]; next[col] = subS; return next })
        setTargetPlots(prev => { const next = [...prev]; next[col] = plotOf(subS).row; return next })
    }

    // ── Submit ────────────────────────────────────────────────────────────────
    // The plan is checked here by the same rule the server applies
    // (validateOrders, under the war's config), then written once as the
    // round's submission; the server answers through the view.
    function handleSubmit() {
        if (warResult !== null || isSubmitDisabled || !view) return
        if (armiesToDeploy) {
            setMessage(`Cannot submit yet. You must deploy ${armiesToDeploy} ${armiesToDeploy === 1 ? 'army' : 'armies'} before submitting.`)
            return
        }
        if (!selfLocalBoardArr.every((bf, i) => !bf.isAttacking || (targetPlots?.[i] ?? null) !== null)) {
            setMessage('Define a target on all corridors where you are attacking.')
            return
        }
        if (dropZones.length < requiredDropZoneCount) {
            const n = requiredDropZoneCount
            setMessage(`You have ${n} brood${n === 1 ? '' : 's'} with an egg — place ${n} drop zone${n === 1 ? '' : 's'} before submitting.`)
            return
        }
        if (pendingDrones.some(d => d.targetS == null)) {
            setMessage('Aim your recon plane before submitting — pick a subplot in its corridor.')
            return
        }
        const orders = {
            board: selfLocalBoardArr,
            targetPlots, targetSubplots, dropZones,
            rockets: pendingRockets,
            drones: pendingDrones,
        }
        const v = validateOrders({ board: view.self.board, homefront: view.self.homefront, yield: view.self.yield }, orders, { config })
        if (!v.ok) { setMessage(v.errors[0]); return }
        planBeforeSubmitRef.current = selfLocalBoardArr
        setSelfLocalBoardArr(selfLocalBoardArr.map(matureCorridor))
        submittingRoundRef.current = view.roundNumber
        setSubmitting(true)
        data.submitOrders({ warId, seat, round: view.roundNumber, uid, orders }).catch(e => {
            console.error('Submit failed', e)
            setSubmitting(false)
            if (planBeforeSubmitRef.current) setSelfLocalBoardArr(planBeforeSubmitRef.current)
            planBeforeSubmitRef.current = null
            setMessage('Those orders could not be sent. Try again.')
        })
    }

    // ── Derived values ────────────────────────────────────────────────────────
    const selfBoard = showingPreResolution && animBoardSelf ? animBoardSelf : selfLocalBoardArr
    const oppBoard = showingPreResolution && (replayBoardOpp || animBoardOpp)
        ? (replayBoardOpp ?? animBoardOpp)
        : oppLocalBoardArr
    const selfLostDisplay = showingPreResolution && animLost ? animLost.self : selfLost
    const oppLostDisplay  = showingPreResolution && animLost ? animLost.opp  : oppLost

    return {
        war, view, seat,
        // Nothing to draw yet: the view, or a reveal set up on a fresh load.
        loading: !view || selfHf == null,

        selfBoard,
        oppBoard,
        selfScore: 0,
        oppScore: 0,
        currentRound: prepared ?? view?.roundNumber ?? 1,
        armiesToDeploy,
        oppArmiesToDeploy: ARMIES_PER_ROUND,
        canDeploy: armiesToDeploy > 0,
        isPaired: true,
        isSubmitDisabled,
        message,
        setMessage,
        onAction,
        handleSubmit,
        handleReveal,

        isAnimating,
        showingPreResolution,
        activeClashId,
        activeClashContext,
        activeClashWinner,
        activeClashSelfStrength,
        activeClashOppStrength,
        activeClashOppTargetIndex,
        activeClashSelfTargetIndex,
        activeClashAbsorbed,
        activeClashAimS,
        activeClashSelfHitInfo: null,
        activeClashSelfHitsLog: null,
        clashResult,

        warResult,

        targetPlots,
        targetSubplots,
        onTargetPlot,

        dropZones,
        requiredDropZoneCount,
        onDropZone,
        placingDropZones,
        resupplyDeliveries,

        revealOverlayUp,
        oppReadyOverlayUp,

        yieldBalance: yieldBalance - pendingSpend,
        onHomefrontAction,
        pendingRockets,
        canAffordRocket: (yieldBalance - pendingSpend) >= rocketCost,
        pendingDrones,
        canAffordDrone: (yieldBalance - pendingSpend) >= droneCost,
        onDroneTarget,
        droneFlights,
        baseBuildFx,

        selfHf,
        oppHf,
        selfLost: selfLostDisplay,
        oppLost: oppLostDisplay,
        onStructureKilled: markAnimKill,
        structuresToWin: war?.killsToWin ?? 1,
        warCapacities: war?.capacities ?? BATTLEFIELD_CAPACITIES,
    }
}
